import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Download } from 'lucide-react';
import api from '@/lib/api';
import AlternadorDeVisao from './AlternadorDeVisao';
import { COR_FUNDO, NOME_COR, coresPresentes, tintaSobre, visaoSalva, type Visao } from './cores';
import { CABECALHO_FIXO, CELULA, CELULA_COR, COLUNA_PRODUTO, LINHA, TITULO_COLUNA_FIXO } from './tabela';
import Preloader from '@/components/Preloader';
import PassosDaCotacao from './PassosDaCotacao';
import { ETAPAS, type Situacao } from './situacao';

type Item = {
  id: number;
  ordem: number;
  produto: string;
  embalagem: string | null;
  quantidade_m: number;
  cores: Record<string, number>;
};

type Proposta = {
  fornecedor: string;
  tipo: string;
  itens_lidos: number;
  itens_vinculados: number;
};

type Cotacao = {
  id: number;
  numero: number;
  descricao: string;
  data_cotacao: string | null;
  status: string;
  condicoes: { frete: string | null; pagamento: string | null; prazo_entrega_dias: number | null };
  itens: Item[];
  rodadas: { id: number; numero: number; descricao: string; propostas: Proposta[] }[];
  situacao: Situacao;
};

const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/**
 * Passo 2: os produtos que vão para orçamento dos fornecedores — o resultado
 * do agrupamento, no formato em que os três já sabem responder.
 */
export default function CotacaoPage() {
  const { cotacaoId } = useParams();
  const [visao, setVisao] = useState<Visao>(visaoSalva);

  const { data: cotacao, isLoading } = useQuery({
    queryKey: ['cotacao', cotacaoId, 'com-itens'],
    queryFn: async () =>
      (await api.get<{ data: Cotacao }>(`/cotacoes/${cotacaoId}?com_itens=1`)).data.data,
  });

  const baixarPlanilha = async () => {
    const resposta = await api.get(`/cotacoes/${cotacaoId}/planilha`, { responseType: 'blob' });
    const url = URL.createObjectURL(resposta.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Cotacao-${cotacao?.numero ?? ''}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading || !cotacao) {
    return <Preloader telaCheia mensagem="Carregando cotação…" className="py-3xl" />;
  }

  const total = cotacao.itens.reduce((soma, item) => soma + item.quantidade_m, 0);
  const cores = coresPresentes(cotacao.itens);
  // O botão segue a etapa em que a cotação está, como na listagem.
  const proximo = ETAPAS[cotacao.situacao.etapa];

  return (
    <div className="px-lg py-lg">
      <PassosDaCotacao cotacao={cotacao} atual="cotacao" />

      <div className="flex flex-wrap items-start justify-between gap-md mb-md">
        <div>
          <h1 className="text-[18px] font-bold text-ink">
            <span className="mr-sm text-accent tabular-nums">Nº {cotacao.numero}</span>
            {cotacao.descricao}
          </h1>
          <p className="text-[13px] text-muted">
            {cotacao.itens.length} linhas · {inteiro.format(total)} metros
            {cotacao.condicoes.frete ? ` · frete ${cotacao.condicoes.frete}` : ''}
            {cotacao.condicoes.pagamento ? ` · ${cotacao.condicoes.pagamento}` : ''}
            {cotacao.condicoes.prazo_entrega_dias ? ` · entrega em ${cotacao.condicoes.prazo_entrega_dias} dias` : ''}
          </p>
        </div>

        <div className="flex gap-sm">
          <button
            type="button"
            onClick={baixarPlanilha}
            className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white"
          >
            <Download size={15} />
            Baixar planilha para os fornecedores
          </button>

          <Link
            to={`/cotacoes/${cotacao.id}/${proximo.destino}`}
            className="inline-flex items-center gap-xs rounded-md border border-border bg-surface px-md py-sm text-[13px] font-bold text-ink hover:border-accent"
          >
            Próximo passo: {proximo.botao.toLowerCase()}
            <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      <div className="mb-sm flex flex-wrap items-center justify-between gap-sm">
        <p className="text-[12px] text-muted">
          {cores.length} {cores.length === 1 ? 'cor usada' : 'cores usadas'} nesta cotação
        </p>
        <AlternadorDeVisao visao={visao} aoTrocar={setVisao} />
      </div>

      {/* Com 59 linhas, o cabeçalho gruda abaixo da barra do topo ao rolar a
          página: descendo até o fim, continua visível de quem é cada coluna. */}
      <div className="rounded-lg border border-border bg-surface overflow-x-auto xl:overflow-visible">
        <table className="w-full text-[13px]">
          <thead className={CABECALHO_FIXO}>
            <tr>
              <th className={`${TITULO_COLUNA_FIXO} w-10 text-left`}>#</th>
              <th className={`${TITULO_COLUNA_FIXO} ${COLUNA_PRODUTO} text-left`}>Produto</th>
              <th className={`${TITULO_COLUNA_FIXO} whitespace-nowrap text-right`}>Quantidade</th>

              {visao === 'colunas' ? (
                cores.map((cor) => (
                  <th
                    key={cor}
                    title={NOME_COR[cor] ?? cor}
                    // A borda é o que faz o branco e o cristal existirem como
                    // célula: sem ela, some no fundo branco da tabela. Sombra, e
                    // não borda, para acompanhar o cabeçalho quando ele gruda.
                    className="px-xs py-sm text-right font-bold normal-case tracking-normal shadow-[inset_-1px_-2px_0_var(--color-border)]"
                    style={{ background: COR_FUNDO[cor] ?? '#cbd5e1', color: tintaSobre(cor) }}
                  >
                    {cor}
                  </th>
                ))
              ) : (
                <th className={`${TITULO_COLUNA_FIXO} text-left`}>Distribuição por cor</th>
              )}

              <th className={`${TITULO_COLUNA_FIXO} text-left`}>Embalagem</th>
            </tr>
          </thead>
          <tbody>
            {cotacao.itens.map((item) => (
              <tr key={item.id} className={LINHA}>
                <td className={`${CELULA} tabular-nums text-muted`}>{item.ordem}</td>
                <td className={`${CELULA} ${COLUNA_PRODUTO} text-ink`}>{item.produto}</td>
                <td className={`${CELULA} whitespace-nowrap text-right font-bold tabular-nums text-ink`}>
                  {inteiro.format(item.quantidade_m)} m
                </td>

                {visao === 'colunas' ? (
                  cores.map((cor) => (
                    <td key={cor} className={`${CELULA_COR} text-ink`}>
                      {item.cores[cor] ? inteiro.format(item.cores[cor]) : ''}
                    </td>
                  ))
                ) : (
                  <td className={CELULA}>
                    <span className="flex flex-wrap gap-xs">
                      {Object.entries(item.cores).map(([cor, quantidade]) => (
                        <span
                          key={cor}
                          title={NOME_COR[cor] ?? cor}
                          className="inline-flex items-center gap-xs rounded border border-border bg-surface px-xs text-[11px] tabular-nums"
                        >
                          <i
                            className="block size-2 rounded-[2px]"
                            style={{ background: COR_FUNDO[cor] ?? '#cbd5e1' }}
                          />
                          {inteiro.format(quantidade)}
                        </span>
                      ))}
                    </span>
                  </td>
                )}

                <td className={CELULA}>
                  <span className="whitespace-nowrap text-[12px] text-muted">
                    {item.embalagem ?? 'a definir'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-sm text-[12px] text-muted">
        O mesmo arquivo vai para os três fornecedores. A divisão por cor fica guardada aqui e volta
        na hora do pedido — o fornecedor cota o produto.
      </p>
    </div>
  );
}
